import React, { useEffect, useState } from 'react';
import 'bootstrap-select';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LoadingSpinner from 'component/LoadingSpinner';
import ProjectService from 'service/ProjectService';
import { ProjectCreateIncompletionReq } from 'domain/payload/request/project/ProjectCreateIncompletionReq';
import { ClientListResp } from 'domain/payload/response/client/ClientListResp';
import { ProjectCreateReq } from 'domain/payload/request/project/ProjectCreateReq';
import { ProjectUpdateReq } from 'domain/payload/request/project/ProjectUpdateReq';
import { Value } from 'domain/model/Value';
import CustomDropdown from 'component/CustomDropdown';
import ClusterCombobox from 'component/ClusterCombobox';
import JobGroupTargetService from 'service/ClientTransmoraService/JobGroupTargetService';

export interface JobGroupTargetFormItem {
  id?: string;
  groupName: string;
  groupDescription?: string;
  targetValue: number;
}


const ProjectForm: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);

  const navigate = useNavigate();
  const projectService = ProjectService();
  const jobGroupTargetService = JobGroupTargetService();

  // INPUT STATE
  const [clientId, setClientId] = useState('');
  const [principalId, setPrincipalId] = useState('');
  const [name, setName] = useState('');
  const [cluster, setCluster] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [instructionFormId, setInstructionFormId] = useState('');
  const [incompletionFormIds, setIncompletionFormIds] = useState<ProjectCreateIncompletionReq[]>([]);
  const [logo, setLogo] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [showMapDetail, setShowMapDetail] = useState(true);
  const [geocodeResult, setGeocodeResult] = useState(false);

  // JOB GROUP TARGET STATE
  const [jobGroupTargets, setJobGroupTargets] = useState<JobGroupTargetFormItem[]>([]);
  const [isLoadingTargets, setIsLoadingTargets] = useState(false);

  // LOADING
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  // CONFIG
  const [clientList, setClientList] = useState<ClientListResp[]>([]);
  const [principalList, setPrincipalList] = useState<Value[]>([]);
  const [formList, setFormList] = useState<Value[]>([]);
  const [clusterOptions, setClusterOptions] = useState<string[]>([]);
  const [isLoadingClusters, setIsLoadingClusters] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingSave(true);
    try {
      let projectId: string | undefined = id;
      const token = profile?.data?.token;

      if (id === "0") {
        const result = await projectCreate();
        projectId = result?.data?.id;
        alert(`Data ${name} successfully created!`);
      } else {
        await projectUpdate();
        alert(`Data ${name} successfully updated!`);
      }

      // upload gambar logo
      if (logo && projectId && projectId !== "0" && token) {
        await projectService.uploadLogo(token, projectId, logo);
      }

      // save job group target
      if (projectId && projectId !== "0" && token) {
        try {
          await jobGroupTargetService.JobGroupTargetSave(token, {
            projectId: projectId,
            targets: jobGroupTargets.map(t => ({
              ...(t.id ? { id: t.id } : {}),
              groupName: t.groupName,
              groupDescription: t.groupDescription?.trim() || null,
              targetValue: Number(t.targetValue) || 0
            }))
          });
        } catch (targetErr: any) {
          console.error('Error saving Job Group Targets:', targetErr);
          alert(`Warning: Gagal menyimpan Job Group Target. ${targetErr.message || targetErr}`);
        }
      }

      navigate("/project");
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat menyimpan data';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const fetchJobGroupTargets = async (projectId: string) => {
    const token = profile?.data?.token;
    if (!token) return;
    setIsLoadingTargets(true);
    try {
      const res = await jobGroupTargetService.JobGroupTargetDetail(token, projectId);
      if (res && res.data && Array.isArray(res.data.targets)) {
        setJobGroupTargets(res.data.targets.map(t => ({
          id: t.id,
          groupName: t.groupName,
          groupDescription: t.groupDescription || '',
          targetValue: t.targetValue
        })));
      }
    } catch (err: any) {
      console.warn('Job Group Target fetch error:', err);
    } finally {
      setIsLoadingTargets(false);
    }
  };

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) {
      alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }
    setIsLoadingDetail(true);
    try {
      const res = await projectService.projectDetail(token, id ?? "0");
      setClientId(res.data?.clientId || '');
      setPrincipalId(res.data?.principalId || '');
      setName(res.data?.name || '');
      setCluster(res.data?.cluster || '');
      setDescription(res.data?.description || '');
      setStartDate(res.data?.startDate || '');
      setEndDate(res.data?.endDate || '');
      setInstructionFormId(res.data?.instruction?.id || '');
      setIncompletionFormIds((res.data?.incompletions || []).map(inc => ({
        reason: inc.reason,
        formId: inc.instruction?.id || ''
      })));
      setPreviewUrl(res.data?.logoUrl || null);
      setIsRunning(res.data?.isRunning || false);
      setShowMapDetail(res.data?.showMapDetail !== undefined ? res.data.showMapDetail : true);
      setGeocodeResult(res.data?.geocodeResult !== undefined ? res.data.geocodeResult : false);

      if (id && id !== "0") {
        fetchJobGroupTargets(id);
      }
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail project';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const fetchConfig = async () => {
    const token = profile?.data.token;
    if (!token) {
      alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }
    setIsLoadingConfig(true);
    try {
      const res = await projectService.projectConfig(token);
      setClientList(res.data?.clientList || []);
      setPrincipalList(res.data?.principalList || []);
      setFormList(res.data?.formList || []);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil konfigurasi project';
      // alert(`Error: ${errorMessage}`);
      console.log(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const fetchClusterConfig = async () => {
    const token = profile?.data?.token;
    if (!token) return;
    setIsLoadingClusters(true);
    try {
      const res = await projectService.projectClusterConfig(token);
      const clusters = Array.isArray(res.data) ? res.data.map(c => c.name) : [];
      setClusterOptions(clusters);
    } catch (err: any) {
      console.warn('Failed to fetch cluster config:', err);
    } finally {
      setIsLoadingClusters(false);
    }
  };

  const projectCreate = async () => {
    const token = profile?.data.token;
    if (!token) {
      alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const request: ProjectCreateReq = {
        name,
        cluster,
        description,
        clientId,
        principalId: principalId ? principalId : undefined,
        startDate,
        endDate,
        instructionFormId,
        incompletionFormIds,
        isRunning,
        showMapDetail,
        geocodeResult
      };
      const result = await projectService.projectCreate(token, request);
      return result;
    } catch (err) {
      console.error(err);
      throw err
    }
  };

  const projectUpdate = async () => {
    const token = profile?.data.token;
    if (!token) {
      alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const request: ProjectUpdateReq = {
        id: id!,
        name,
        cluster,
        description,
        clientId,
        principalId: principalId ? principalId : undefined,
        startDate,
        endDate,
        instructionFormId,
        incompletionFormIds,
        isRunning,
        showMapDetail,
        geocodeResult
      };
      console.log(request)
      await projectService.projectUpdate(token, request);
    } catch (err) {
      console.error(err);
      throw err
    }
  };

  const handleAddIncompletion = () => {
    setIncompletionFormIds(prev => [...prev, { reason: '', formId: '' }]);
  };

  const handleRemoveIncompletion = (index: number) => {
    setIncompletionFormIds(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateIncompletion = (index: number, key: keyof ProjectCreateIncompletionReq, value: string) => {
    setIncompletionFormIds(prev => {
      const updated = [...prev];
      updated[index][key] = value;
      return updated;
    });
  };

  const handleAddJobGroupTarget = () => {
    setJobGroupTargets(prev => [...prev, { groupName: '', groupDescription: '', targetValue: 0 }]);
  };

  const handleRemoveJobGroupTarget = (index: number) => {
    setJobGroupTargets(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateJobGroupTarget = (index: number, field: keyof JobGroupTargetFormItem, value: any) => {
    setJobGroupTargets(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: field === 'targetValue' ? (value === '' ? '' : Number(value)) : value
      };
      return updated;
    });
  };


  // gambar upload
  const MAX_SIZE = 100 * 1024;

  const handleFileChange = (file: File | null) => {
    if (!file) {
      setLogo(null);
      setPreviewUrl(null);
      return;
    }

    if (file.type !== "image/png") {
      alert("Format gambar harus .png");
      return;
    }
    if (file.size > MAX_SIZE) {
      alert("Ukuran file maksimal 100 KB");
      return;
    }
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width !== img.height) {
        alert("Gambar harus rasio 1:1");
        URL.revokeObjectURL(objectUrl);
        return;
      }

      setLogo(file);
      setPreviewUrl(objectUrl);
    };

    img.src = objectUrl;
  };

  useEffect(() => {
    if (id !== "0") fetchDetail();
    fetchConfig();
    fetchClusterConfig();
  }, []);

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8', overflowX: 'hidden' }}>
      <div className="m-4">
        <h4 className="mb-4">{id === "0" ? "Add Project" : "Edit Project"}</h4>
        <div className="p-4 rounded shadow-sm" style={{ background: 'white' }}>
          {isLoadingDetail ? <LoadingSpinner /> :
            <form onSubmit={handleSubmit}>
              <div className="row g-4">
                {/* Left Column: Basic Information & Timeline */}
                <div className="col-12 col-lg-6">
                  <div className="mb-4">
                    <h5 className="mb-4">Basic Information</h5>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Client (Pengendali Project)</label>
                      <CustomDropdown
                        value={clientId}
                        options={clientList.map(c => ({
                          id: c.id,
                          name: c.name
                        }))}
                        onChange={(option) => setClientId(String(option.id))}
                        containerClassName="w-100"
                        buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Principal (Pemilik Project / Opsional)</label>
                      <CustomDropdown
                        value={principalId}
                        options={[
                          { id: '', name: '-- Tanpa Principal (Internal Project) --' },
                          ...principalList.map(p => ({
                            id: p.id,
                            name: p.name
                          }))
                        ]}
                        onChange={(option) => setPrincipalId(String(option.id))}
                        containerClassName="w-100"
                        buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      />
                      <small className="text-muted d-block mt-1">
                        Pilih jika project ini dimiliki oleh Principal/Brand tertentu untuk dilaporkan di Transmore Web Client.
                      </small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Name</label>
                      <input type="text" className="form-control form-control-compact" value={name} onChange={e => setName(e.target.value)} required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Cluster</label>
                      <ClusterCombobox
                        value={cluster}
                        onChange={setCluster}
                        options={clusterOptions}
                        isLoading={isLoadingClusters}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Description</label>
                      <input type="text" className="form-control form-control-compact" value={description} onChange={e => setDescription(e.target.value)} required />
                    </div>
                  </div>

                  <div className="mb-4">
                    <h5 className="mb-4">Project Timeline</h5>
                    <div className="mb-3 row">
                      <div className="col-6">
                        <label className="form-label fw-semibold">Start</label>
                        <input type="date" className="form-control form-control-compact" value={startDate} onChange={e => setStartDate(e.target.value)} required />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-semibold">End</label>
                        <input type="date" className="form-control form-control-compact" value={endDate} onChange={e => setEndDate(e.target.value)} required />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Form Instructions & Incompletions */}
                <div className="col-12 col-lg-6 mb-4">
                  <div className="mb-4">
                    <h5 className="mb-4">Form Instructions & Logo</h5>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Form</label>
                      <CustomDropdown
                        value={instructionFormId}
                        options={formList.map(c => ({
                          id: c.id,
                          name: c.name
                        }))}
                        onChange={(option) => setInstructionFormId(String(option.id))}
                        containerClassName="w-100"
                        buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      />
                    </div>
                    <div className='mb-3'>
                      <label className="form-label fw-semibold">Logo</label>
                      <div className='d-flex gap-3'>
                        {previewUrl ?
                          <img src={previewUrl} className='rounded col-auto border' alt="Logo Preview" style={{ width: '120px', height: '120px', objectFit: 'contain' }} />
                          :
                          <span className='rounded col-auto border text-center d-flex align-items-center justify-content-center' style={{ width: '120px', height: '120px' }}>Belum Ada Logo</span>
                        }
                        <div className='col'>
                          <input
                            type="file"
                            className="form-control form-control-compact"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold d-block">Is Running</label>
                      <div style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>
                        <div className="form-check form-switch d-inline-flex align-items-center" style={{ transform: 'scale(1.5)', transformOrigin: 'left center', margin: 0 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="isRunningSwitch"
                            checked={isRunning}
                            onChange={(e) => setIsRunning(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          <label className="form-check-label ms-2" htmlFor="isRunningSwitch" style={{ cursor: 'pointer' }}>
                            <span className={`badge ${isRunning ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.7rem' }}>
                              {isRunning ? 'Running' : 'Stopped'}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold d-block">Show Map Detail</label>
                      <div style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>
                        <div className="form-check form-switch d-inline-flex align-items-center" style={{ transform: 'scale(1.5)', transformOrigin: 'left center', margin: 0 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="showMapDetailSwitch"
                            checked={showMapDetail}
                            onChange={(e) => setShowMapDetail(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          <label className="form-check-label ms-2" htmlFor="showMapDetailSwitch" style={{ cursor: 'pointer' }}>
                            <span className={`badge ${showMapDetail ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.7rem' }}>
                              {showMapDetail ? 'Enabled' : 'Disabled'}
                            </span>
                          </label>
                        </div>
                      </div>
                      <div className="form-text text-muted" style={{ fontSize: '0.825rem', marginTop: '4px' }}>
                        If disabled, location coordinates will not be displayed on the surveyor application.
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold d-block">Geocode Result</label>
                      <div style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>
                        <div className="form-check form-switch d-inline-flex align-items-center" style={{ transform: 'scale(1.5)', transformOrigin: 'left center', margin: 0 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="geocodeResultSwitch"
                            checked={geocodeResult}
                            onChange={(e) => setGeocodeResult(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          <label className="form-check-label ms-2" htmlFor="geocodeResultSwitch" style={{ cursor: 'pointer' }}>
                            <span className={`badge ${geocodeResult ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.7rem' }}>
                              {geocodeResult ? 'Enabled' : 'Disabled'}
                            </span>
                          </label>
                        </div>
                      </div>
                      <div className="form-text text-muted" style={{ fontSize: '0.825rem', marginTop: '4px' }}>
                        Automatically detects and resolves street addresses from coordinates on submitted results.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 mt-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="mb-0">Job Target by Group Area</h5>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleAddJobGroupTarget}
                    >
                      <i className="fa-solid fa-plus me-1" /> Add Group Target
                    </button>
                  </div>
                  {isLoadingTargets ? (
                    <LoadingSpinner />
                  ) : (
                    <div className="table-responsive">
                      {jobGroupTargets.length === 0 ? (
                        <div className="text-muted fst-italic p-3 text-center border rounded bg-light">
                          Belum ada target group area. Klik "Add Group Target" untuk menambahkan.
                        </div>
                      ) : (
                        <table className="table table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '30%' }}>Group Area Name</th>
                              <th style={{ width: '40%' }}>Group Description</th>
                              <th style={{ width: '20%' }}>Target Value</th>
                              <th style={{ width: '10%' }} className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {jobGroupTargets.map((target, idx) => (
                              <tr key={target.id || idx}>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-compact"
                                    placeholder="e.g. Sampling Product"
                                    value={target.groupName}
                                    onChange={(e) => handleUpdateJobGroupTarget(idx, 'groupName', e.target.value)}
                                    required
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-compact"
                                    placeholder="e.g. Target khusus tim sampling..."
                                    value={target.groupDescription ?? ''}
                                    onChange={(e) => handleUpdateJobGroupTarget(idx, 'groupDescription', e.target.value)}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min="0"
                                    className="form-control form-control-compact"
                                    placeholder="e.g. 150"
                                    value={target.targetValue}
                                    onChange={(e) => handleUpdateJobGroupTarget(idx, 'targetValue', e.target.value)}
                                    required
                                  />
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-small-compact"
                                    onClick={() => handleRemoveJobGroupTarget(idx)}
                                    title="Hapus Target"
                                  >
                                    <i className="fa-solid fa-trash" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="w-100 d-flex justify-content-end">
                <Link to="/project">
                  <button type="button" className="btn btn-outline-primary">Cancel</button>
                </Link>
                <button type="submit" className="btn btn-primary ms-2" disabled={isLoadingSave}>
                  {isLoadingSave && <LoadingSpinner reverse />}
                  Save
                </button>
              </div>
            </form>
          }
        </div>
      </div>
    </div>
  );
};

export default ProjectForm;